import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { academicService } from '../services/academicService';

export default function HomePage() {
  const [levels, setLevels] = useState([]);
  const [userLevels, setUserLevels] = useState([]);
  const [academicClasses, setAcademicClasses] = useState([]);
  const [hasAcademicSchema, setHasAcademicSchema] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    const authData = sessionStorage.getItem('user_auth');
    if (authData) {
      const { user } = JSON.parse(authData);
      setUserData(user);
    }

    const timeInterval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timeInterval);
  }, []);

  useEffect(() => {
    if (userData) {
      fetchLevels();
    }
  }, [userData]);

  const fetchLevels = async () => {
    try {
      setLoading(true);

      if (userData?.role === 'student') {
        const { data: enrollmentData, error: enrollmentError } = await supabase
          .from('student_enrollment')
          .select('level_code, level:levels(*)')
          .eq('student_id', userData.id);

        if (enrollmentError) throw enrollmentError;
        setUserLevels((enrollmentData || []).map(item => item.level).filter(Boolean));
      } else if (userData?.role === 'instructor' || userData?.role === 'admin') {
        const { data: levelsData, error: levelsError } = await supabase
          .from('levels')
          .select('*')
          .or(`teacher_id.eq.${userData.id},secondary_teacher_id.eq.${userData.id}`)
          .order('name', { ascending: true });

        if (levelsError) throw levelsError;
        setLevels(levelsData || []);
      } else {
        const { data: levelsData, error: levelsError } = await supabase
          .from('levels')
          .select('*')
          .order('name', { ascending: true });

        if (levelsError) throw levelsError;
        setLevels(levelsData || []);
      }

      const ready = await academicService.isReady();
      setHasAcademicSchema(ready);
      if (ready) {
        const classRows = await academicService.listClasses();
        setAcademicClasses(classRows);
      } else {
        setAcademicClasses([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getTimeString = () => {
    return currentTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 animate-fade-in">
          <div className="w-16 h-16 loading-spinner"></div>
          <div className="text-center">
            <p className="text-primary-600 font-semibold text-lg">Loading Dashboard...</p>
            <p className="text-neutral-500 text-sm mt-1">Preparing your personalized experience</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center container-mobile">
        <div className="card p-8 max-w-md w-full text-center animate-slide-up">
          <div className="w-16 h-16 bg-error-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-error-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-neutral-900 mb-2">Oops! Something went wrong</h2>
          <p className="text-neutral-600 mb-6">Error loading dashboard: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const displayLevels = userData?.role === 'student' ? userLevels : levels;
  const classByLevelCode = new Map(
    academicClasses.map((classItem) => [classItem.legacy_level_code, classItem])
  );
  const decoratedLevels = displayLevels
    .filter(Boolean)
    .map((level) => ({
      ...level,
      academicClass: classByLevelCode.get(level.code) || null
    }));
  const currentTermLevels = decoratedLevels.filter((level) => level.academicClass?.term?.is_current);
  const pastLevels = decoratedLevels.filter((level) => !level.academicClass?.term?.is_current);
  const showSplit = hasAcademicSchema && (currentTermLevels.length > 0 || pastLevels.some((level) => level.academicClass));
  const activeLevels = decoratedLevels.filter((level) => level.active !== false);
  const inactiveLevels = decoratedLevels.filter((level) => level.active === false);

  const renderLevelCard = (level, index) => (
    <div
      key={level.id || level.code}
      onClick={() => navigate(`/level/${level.code}`)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(`/level/${level.code}`);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Open ${level.academicClass?.name || level.name}`}
      className="group cursor-pointer animate-slide-up"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className={`bg-white rounded-3xl p-6 border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
        level.active === false || level.academicClass?.status === 'completed'
          ? 'border-neutral-300 opacity-60 hover:border-neutral-400'
          : 'border-neutral-200 hover:border-primary-300'
      }`}>
        <div className="flex items-start justify-between mb-4">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200 shadow-lg ${
            level.active === false || level.academicClass?.status === 'completed'
              ? 'bg-gradient-to-br from-neutral-400 to-neutral-500'
              : 'bg-gradient-to-br from-brand-red to-brand-gold'
          }`}>
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          {(level.active === false || level.academicClass?.status === 'completed') && (
            <span className="px-2 py-1 text-xs font-medium bg-neutral-200 text-neutral-600 rounded-full">
              {level.academicClass?.status === 'completed' ? 'Completed' : 'Inactive'}
            </span>
          )}
        </div>

        <h3 className={`text-lg font-bold mb-3 group-hover:text-primary-700 transition-colors duration-200 ${
          level.active === false ? 'text-neutral-500' : 'text-neutral-900'
        }`}>
          {level.academicClass?.name || level.name}
        </h3>

        <div className="space-y-2 mb-4">
          {level.academicClass?.term?.name && (
            <div className="text-sm text-neutral-600">
              {level.academicClass.term.name}
            </div>
          )}
          {level.academicClass?.subjects?.length > 0 && (
            <div className="text-sm text-neutral-700">
              {level.academicClass.subjects.map((subject) => subject.name).join(' · ')}
            </div>
          )}
          {!level.academicClass && level.period && (
            <div className="flex items-center text-sm text-neutral-500">
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Period: {level.period}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
          <span className={`text-sm font-medium group-hover:text-primary-800 ${
            level.active === false ? 'text-neutral-400' : 'text-brand-red'
          }`}>
            {userData?.role === 'student' ? 'View Class & Grades' : 'View Class'}
          </span>
          <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center group-hover:bg-primary-200 transition-colors duration-200">
            <svg className="w-3 h-3 text-brand-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSection = (title, description, items) => {
    if (!items.length) return null;

    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-1">{title}</h2>
            <p className="text-neutral-600 text-sm sm:text-base">{description}</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-sm text-neutral-500">
            {items.length}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {items.map((level, index) => renderLevelCard(level, index))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="container-mobile py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gradient mb-1">
              {getGreeting()}, {userData?.name?.split(' ')[0] || 'Student'}!
            </h1>
            <p className="text-neutral-600 text-sm sm:text-base">
              {getTimeString()} • {currentTime.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {userData?.role === 'student' && (
              <button
                type="button"
                onClick={() => navigate('/dashboard/transcript')}
                className="btn-secondary px-4 py-2 text-sm font-semibold"
              >
                Transcript
              </button>
            )}
            {userData?.role === 'admin' && (
              <button
                onClick={() => navigate('/dashboard/admin')}
                className="btn-primary px-4 py-2 text-sm font-semibold flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Admin Dashboard
              </button>
            )}
          </div>
        </div>
      </header>

      <section className="container-mobile pb-12 sm:pb-16 lg:pb-20">
        {showSplit ? (
          <>
            {renderSection(
              'This term',
              'Teacher classes with subjects for the current semester',
              currentTermLevels
            )}
            {renderSection(
              'Past classes',
              'Previous semester levels stay here so last term’s grades can still be finished',
              pastLevels
            )}
          </>
        ) : (
          <>
            {renderSection(
              'Active Classes',
              userData?.role === 'student'
                ? 'Select a class to view updates, assignments, grades, and attendance'
                : 'Select a class to view or post updates',
              activeLevels
            )}
            {renderSection(
              'Inactive Classes',
              'Archived or inactive classes',
              inactiveLevels
            )}
          </>
        )}

        {decoratedLevels.length === 0 && (
          <div className="text-center py-16 animate-fade-in">
            <div className="w-24 h-24 bg-gradient-to-br from-neutral-100 to-neutral-200 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-neutral-900 mb-2">
              {userData?.role === 'student' ? 'No classes enrolled' : 'No classes available'}
            </h3>
            <p className="text-neutral-500 max-w-md mx-auto">
              {userData?.role === 'student'
                ? 'You are not enrolled in any classes yet. Contact your teacher for access.'
                : 'No classroom levels have been created yet.'
              }
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
